import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Wind, Leaf, Clock, Youtube, Play, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function YogaGuide() {
  const navigate = useNavigate();

  const poses = [
    { name: "Mountain Pose (Tadasana)", duration: "5 breaths", benefit: "Foundation for all standing poses, improves posture", instructions: "Stand tall, feet together, arms at sides. Ground through feet, engage thighs, lengthen spine, relax shoulders." },
    { name: "Cat-Cow Stretch", duration: "8-10 rounds", benefit: "Warms spine, releases back tension", instructions: "On hands and knees, alternate between arching back (cow) and rounding spine (cat) with breath." },
    { name: "Downward Dog", duration: "5-8 breaths", benefit: "Stretches hamstrings, shoulders, calves; energizes body", instructions: "From hands and knees, lift hips up and back. Press hands firmly, reach heels toward floor." },
    { name: "Warrior I", duration: "5 breaths each side", benefit: "Strengthens legs, opens hips and chest", instructions: "Step one foot back, turn it out slightly. Bend front knee, raise arms overhead. Square hips forward." },
    { name: "Warrior II", duration: "5 breaths each side", benefit: "Builds leg strength, improves focus", instructions: "From Warrior I, open hips and arms to the side. Gaze over front fingertips." },
    { name: "Triangle Pose", duration: "5 breaths each side", benefit: "Stretches legs, opens chest, strengthens core", instructions: "Straighten front leg, reach forward then down. Top arm reaches up, gaze follows." },
    { name: "Tree Pose", duration: "5-8 breaths each side", benefit: "Improves balance, strengthens ankles", instructions: "Stand on one leg, place other foot on inner thigh or calf (not knee). Hands at heart or overhead." },
    { name: "Seated Forward Fold", duration: "8-10 breaths", benefit: "Calms mind, stretches spine and hamstrings", instructions: "Sit with legs extended. Hinge at hips, reach toward feet. Keep spine long." },
    { name: "Bridge Pose", duration: "5 breaths", benefit: "Opens chest, strengthens glutes", instructions: "Lie on back, feet flat. Press into feet, lift hips. Clasp hands under back." },
    { name: "Corpse Pose (Savasana)", duration: "3-5 minutes", benefit: "Deep relaxation, integrates practice", instructions: "Lie flat, arms at sides, palms up. Close eyes, release all effort. Just breathe." }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">Week 4 Guide</span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">Yoga for Beginners</h1>
          <p className="text-xl text-secondary-600">A 30-Minute Flow You Can Do at Home</p>
        </div>

        <div className="rounded-2xl overflow-hidden shadow-xl mb-8">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/b182c6e0-1353-47f6-8a97-d98fdf4ac183/images/eeef366cedc961922eadb48164964a854e035d33fd43ad33eea7dd506f5c1d0e.png"
            alt="Yoga Practice"
            className="w-full h-auto object-cover"
          />
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Yoga in Your 40s</h2>
          <p className="text-secondary-700 mb-4">
            Yoga offers unique benefits for women over 40: it maintains flexibility as bodies naturally stiffen, builds bone density through weight-bearing poses, reduces stress hormones, and improves balance—reducing fall risk as we age.
          </p>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { icon: Leaf, title: "Flexibility", desc: "Counteracts stiffness" },
              { icon: Heart, title: "Heart Health", desc: "Lowers blood pressure" },
              { icon: Wind, title: "Stress Relief", desc: "Calms nervous system" },
              { icon: Clock, title: "Any Time", desc: "Morning or evening" }
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-purple-50 rounded-xl">
                <item.icon className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                <h3 className="font-bold text-secondary-900 text-sm">{item.title}</h3>
                <p className="text-xs text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* YouTube Beginner Videos Section */}
        <section className="bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl p-8 border-2 border-red-200 mb-6">
          <div className="flex items-start gap-4 mb-6">
            <div className="bg-red-500 p-3 rounded-full shrink-0">
              <Youtube className="w-8 h-8 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-2">
                New to Yoga? Follow Along on YouTube!
              </h2>
              <p className="text-secondary-700">
                Whether you're a complete beginner or returning after a break, YouTube has thousands of free beginner-friendly yoga videos you can follow along with at home.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 mb-6 border border-red-100">
            <h3 className="font-bold text-secondary-900 mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-red-500" />
              Why Follow a Video?
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</span>
                <span className="text-secondary-700"><strong>Watch & Copy:</strong> See exactly how each pose should look and move along in real-time</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</span>
                <span className="text-secondary-700"><strong>Get Useful Tips:</strong> Instructors share modifications, alignment cues, and breathing reminders</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</span>
                <span className="text-secondary-700"><strong>Stay Motivated:</strong> Having a guide helps you stay focused for the full session</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="bg-red-100 text-red-600 rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</span>
                <span className="text-secondary-700"><strong>Variety:</strong> Try different instructors and styles until you find what resonates with you</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-secondary-900 mb-3 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Start Small, Build Up
            </h3>
            <p className="text-secondary-700 mb-4">
              You don't need to start with 30 minutes! Here's a gentle progression:
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-purple-600 mb-1">10 min</p>
                <p className="text-sm text-secondary-600">Start here if you're brand new</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-purple-600 mb-1">20 min</p>
                <p className="text-sm text-secondary-600">Once you're comfortable</p>
              </div>
              <div className="bg-white rounded-lg p-4 text-center">
                <p className="text-3xl font-bold text-purple-600 mb-1">30 min</p>
                <p className="text-sm text-secondary-600">Your goal to work toward</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-purple-200">
            <h3 className="font-bold text-secondary-900 mb-3">💡 How to Find Great Beginner Videos</h3>
            <p className="text-secondary-700 mb-4">
              Search YouTube for terms like:
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {[
                "Yoga for complete beginners",
                "10 minute gentle yoga",
                "Yoga for women over 40",
                "Beginner yoga flow",
                "Morning yoga stretch",
                "Yoga for flexibility beginners"
              ].map((term, i) => (
                <span key={i} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">
                  "{term}"
                </span>
              ))}
            </div>
            <p className="text-secondary-600 text-sm italic">
              Popular beginner-friendly channels include Yoga With Adriene, Sarah Beth Yoga, and Yoga With Kassandra—but explore and find an instructor whose voice and pace work for you!
            </p>
          </div>

          <div className="mt-6 p-4 bg-green-100 rounded-xl border border-green-300">
            <p className="text-green-800 font-medium text-center">
              🌟 <strong>Your Practice Plan:</strong> Aim for yoga <strong>daily or several times a week</strong>. 
              Start with 10-minute videos and gradually increase to 30 minutes as you build strength and flexibility!
            </p>
          </div>
        </section>

        <section className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-2xl p-6 text-white mb-6">
          <h3 className="font-bold text-lg">Your 30-Minute Flow</h3>
          <p className="text-purple-100">Move through each pose mindfully. Honor where your body is today.</p>
        </section>

        <section className="space-y-4 mb-6">
          {poses.map((pose, i) => (
            <div key={i} className="bg-white rounded-xl p-5 shadow-md border border-gray-100">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-bold text-sm">{i+1}</span>
                  <h3 className="font-bold text-secondary-900">{pose.name}</h3>
                </div>
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs">{pose.duration}</span>
              </div>
              <p className="text-secondary-700 text-sm mb-2">{pose.instructions}</p>
              <p className="text-purple-700 text-xs bg-purple-50 p-2 rounded"><strong>Benefit:</strong> {pose.benefit}</p>
            </div>
          ))}
        </section>

        <section className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-8 border-2 border-purple-200">
          <h2 className="text-xl font-serif font-bold text-secondary-900 mb-4">Tips for Your Practice</h2>
          <ul className="space-y-2">
            {[
              "Use props (blocks, blankets, straps) to make poses accessible",
              "Never force a stretch—find your 'edge' without pain",
              "Breathe slowly and deeply through your nose",
              "It's okay to rest in Child's Pose whenever needed",
              "Consistency matters more than intensity—even 10 minutes helps",
              "Morning yoga energizes; evening yoga relaxes"
            ].map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-secondary-700">
                <span className="text-purple-500">✓</span>{tip}
              </li>
            ))}
          </ul>
        </section>
      </main>
    </div>
  );
}

export default YogaGuide;
